/**
 * The CRF domain vocabulary the six screens share — stages, segments, point
 * statuses. Each mapping lives HERE and only here, so a wording change is one
 * edit and the screens cannot drift apart.
 *
 * ⚠ WITH ONE EXCEPTION, AND IT IS THE OWNER'S: the customization vocabulary is
 * NOT here any more. See the block below `CRF_COMPLETED_STAGES`. What belongs in
 * this file is wording THIS CLIENT owns; a list the server owns does not.
 */

/**
 * WHICH KIND OF WORK A RECORD IS — `crfs.work_type`, the Projects discriminator
 * (migration 37, `apps/api/src/entities/crf.entity.ts`'s `WORK_TYPES`).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚠ A HAND COPY, DECLARED ONCE, AND PINNED BY A TEST — THE HOUSE ANSWER
 * ═══════════════════════════════════════════════════════════════════════════
 * `WORK_TYPES` lives on the API's `Crf` entity, not in `@crf/shared`, so this app
 * cannot import it: the two are separate compilation units and `@crf/shared` is
 * the only bridge. Rather than typing `'crf' | 'project'` inline on each of the
 * five register row shapes plus the work order and the CRF — seven independent
 * copies, which is precisely the drift `RENEWAL_SOURCES`' own header describes —
 * it is declared HERE once and held to the server by `work-type-mirror.test.ts`,
 * the same mechanism `renewals/source-mirror.test.ts` uses and for the same
 * stated reason: the coupling across the app boundary is a red TEST, not a red
 * compile, and a hand copy with no gate is a hand copy that will drift.
 *
 * ⚠ THIS IS NOT THE `crf_modules` MISTAKE RETURNING. That list was a copy of a
 * vocabulary the OWNER edits — words, in a table, changeable without a release,
 * so the server had to send them. `work_type` is a two-member ENUM in a schema
 * migration: it changes only when somebody ships a migration, and when they do,
 * the mirror test reddens and names the file. Different kind of list, different
 * correct answer.
 */
export const WORK_TYPES = ['crf', 'project'] as const;
export type WorkType = (typeof WORK_TYPES)[number];

/**
 * THE BADGE WORD, and it is a WORD rather than only a colour on purpose.
 *
 * The distinction between a CRF and a project has to survive a screenshot pasted
 * into a chat, a colour-blind reader, and a CSV export opened in Excel — three
 * channels a tint does not reach. So every surface that distinguishes the two
 * renders this text, and the tint (where there is one) is the redundant half.
 *
 * Total over the union, so a third work type cannot be added without somebody
 * deciding what an operator should call it.
 */
export const WORK_TYPE_LABEL: Readonly<Record<WorkType, string>> = {
  crf: 'CRF',
  project: 'Project',
};

/**
 * THE MANUALS VOCABULARY — the legacy's TWO HARDCODED DOCUMENT TYPES.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHERE THESE TWO WORDS COME FROM, AND WHY THEY ARE SPELLED LIKE THAT
 * ═══════════════════════════════════════════════════════════════════════════
 * The legacy verdict, read out of git history (`4d3127f:docs/REPORT-LATEST.md`
 * §5): *"Two hardcoded types in the legacy ("Screen Short" / "Final Manual")"*.
 * "Screen Short" is the LEGACY'S OWN wording, not a typo this file is repeating
 * by accident — it is preserved because the register exists to be recognised by
 * the people who used that screen. Renaming it to "Screenshot" is an owner call.
 *
 * ⚠ THE SAME HAND-COPY BARGAIN AS `WORK_TYPES` ABOVE, FOR THE SAME REASON.
 * `ATTACHMENT_DOC_TYPES` lives on the API's `Attachment` entity (migration 55),
 * not in `@crf/shared`, so this app declares its own copy and pays for it with
 * `manual-doc-type-mirror.test.ts` — a red TEST when the server's ENUM moves,
 * not a red compile. A third type can only arrive by migration, and when it does
 * that test reddens and names the file to edit.
 */
export const MANUAL_DOC_TYPES = ['screen_short', 'final_manual'] as const;
export type ManualDocType = (typeof MANUAL_DOC_TYPES)[number];

/**
 * The register's headings, total over the union so a third type cannot ship
 * without somebody deciding what an operator should call it.
 */
export const MANUAL_DOC_TYPE_LABEL: Readonly<Record<ManualDocType, string>> = {
  screen_short: 'Screen Short',
  final_manual: 'Final Manual',
};

/**
 * The nine `crfs.status` stages → what a human reads on the pill.
 *
 * Labels follow the DESIGN's stage language where the design shows one
 * (12:582 rows read "Pending Confirmation"/"Advance Pending"; the record header
 * reads "In Development") and the enum's own word elsewhere. Tones ride the
 * existing StagePill vocabulary — no new colors are invented here.
 */
export const CRF_STAGES: Record<
  string,
  { label: string; tone: 'violet' | 'amber' | 'blue' | 'green' | 'slate' | 'red' }
> = {
  draft: { label: 'Pending Confirmation', tone: 'violet' },
  confirmed: { label: 'Confirmed', tone: 'blue' },
  in_work: { label: 'In Development', tone: 'blue' },
  in_testing: { label: 'In Testing', tone: 'amber' },
  deployed: { label: 'Deployed', tone: 'green' },
  signed_off: { label: 'Signed Off', tone: 'green' },
  billed: { label: 'Billed', tone: 'green' },
  closed: { label: 'Closed', tone: 'slate' },
  cancelled: { label: 'Cancelled', tone: 'red' },
};

/**
 * A1's Pending / Completed segments — THE bucket mapping, decided once (OQ-A4).
 *
 * "Pending" is everything still moving: awaiting the customer, being built,
 * being tested. "Completed" is everything past deployment plus the two terminal
 * states. `cancelled` sits in Completed because the design's segment counts
 * (Pending 6 / Completed 18, of 24 records) partition ALL records — a row that
 * belonged to neither bucket would make the two tabs disagree with the header's
 * total, which is the quiet lie rule 11 exists to prevent.
 */
export const CRF_PENDING_STAGES = ['draft', 'confirmed', 'in_work', 'in_testing'] as const;
export const CRF_COMPLETED_STAGES = [
  'deployed',
  'signed_off',
  'billed',
  'closed',
  'cancelled',
] as const;

/**
 * ⚠ THE MODULE VOCABULARY IS GONE FROM THIS FILE, AND ITS ABSENCE IS THE POINT.
 *
 * What stood here was a hand copy of the API's four `CRF_MODULES` keys, whose own
 * docblock admitted that "this list agreeing with the server is load-bearing".
 * That was the second of two declarations of one four-word list — the mirror the
 * owner named when he ruled (Q8, 2026-08-14): *"two lists that must stay in step
 * is a mirror, and mirrors drift"*, *"two vocabularies for one idea is the exact
 * defect I named"*.
 *
 * Under migration 38 the vocabulary is a TABLE the owner edits without a release.
 * There is therefore nothing to transcribe, no label lookup to keep in step, and
 * no `@IsIn` on the server to agree with: **the server sends the words.** A
 * platform arrives as `{ platform_id, name, detail }`, and the picker's options
 * are fetched (`fetchCrfPlatformOptions`), never listed here.
 *
 * If a future reader finds themselves adding `const PLATFORMS = [...]` to this
 * file to avoid a fetch, that is the defect returning — read this paragraph first.
 */

/**
 * A1's PLATFORMS cell: "Invoice", "Invoice · Report", "Invoice +2" — the design
 * shows at most two names before collapsing to a count.
 *
 * It takes NAMES, not keys, because the words now come from the server. The
 * function is pure formatting and holds no vocabulary of its own.
 */
export function platformsSummary(names: readonly string[]): string {
  const first = names[0];
  if (first === undefined) return '—';
  if (names.length <= 2) return names.join(' · ');
  return `${first} +${names.length - 1}`;
}

/**
 * The A6 grid's three CHOOSABLE statuses (38:1144). `pending` is the unset
 * state — a row nobody has decided — and is deliberately not offered as a
 * button: undecided is the absence of a choice, not a fourth choice.
 */
export const POINT_DECISIONS = [
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'not_confirmed', label: 'Not confirmed' },
  { key: 'on_hold', label: 'On hold' },
] as const;

/** The design's footer rule, verbatim (38:8234) — mirrored client-side. */
export function pointNeedsRemark(confirmation: string, remark: string | null | undefined): boolean {
  return (
    (confirmation === 'not_confirmed' || confirmation === 'on_hold') &&
    (remark === null || remark === undefined || remark.trim() === '')
  );
}
